import * as Sentry from '@sentry/react';
import { browserTracingIntegration } from '@sentry/react';
import { CONFIG } from './config';

/**
 * Initialize Sentry with the provided DSN and configuration
 * @param dsn The Sentry DSN (Data Source Name)
 */
export function initSentry(dsn: string): void {
  if (!dsn) {
    console.warn('Sentry initialization skipped: No DSN provided');
    return;
  }

  try {
    Sentry.init({
      dsn,
      integrations: [
        // Use browserTracingIntegration() from @sentry/react 9.x
        browserTracingIntegration(),
      ],

      // Set tracesSampleRate to 1.0 to capture 100% of transactions for performance monitoring
      // We recommend adjusting this value in production
      tracesSampleRate: CONFIG.isProduction ? 0.2 : 1.0,

      // Only send errors in production unless explicitly enabled for development
      enabled: CONFIG.isProduction || import.meta.env.VITE_ENABLE_SENTRY_IN_DEV === 'true',

      // Set environment
      environment: CONFIG.isProduction ? 'production' : 'development',

      // Customize how we collect ip addresses and other PII
      beforeSend(event) {
        // Patterns for errors we don't want to report (network issues, expected behavior)
        const ignoreMessagePatterns = [
          /network error/i,
          /failed to fetch/i,
          /load failed/i,
          /JWT expired/i,
          /PGRST301/i,
          /signal is aborted/i, // React Query request cancellation
          /aborted without reason/i, // DOMException abort message
        ];

        // Error types that indicate expected abort behavior (exact match)
        const ignoreErrorTypes = ['AbortError'];

        if (event.exception && event.exception.values && event.exception.values[0]) {
          const errorValue = event.exception.values[0];
          const errorMessage = errorValue.value || '';
          const errorType = errorValue.type || '';

          // Check for ignored error types (exact match to avoid false positives)
          if (ignoreErrorTypes.includes(errorType)) {
            return null;
          }

          // Check message patterns
          if (ignoreMessagePatterns.some(pattern => pattern.test(errorMessage))) {
            return null;
          }
        }

        // Check for JWT expired errors in the event extra data
        if (event.extra && typeof event.extra === 'object') {
          const extraAsString = JSON.stringify(event.extra);
          if (extraAsString.includes('JWT expired') || extraAsString.includes('PGRST301')) {
            return null;
          }
        }

        return event;
      },

      // Set release version when available
      release: import.meta.env.VITE_APP_VERSION || 'development',
    });
  } catch (error) {
    // If Sentry initialization fails, log it but don't break the app
    console.warn('Sentry initialization failed:', error);
  }
}

/**
 * Wrap the given error in a Sentry scope with context and report it
 * @param error The error to report
 * @param context Additional context information
 * @param level The severity level
 */
export function reportToSentry(
  error: Error | unknown,
  context: Record<string, unknown> = {},
  level: Sentry.SeverityLevel = 'error'
): void {
  // Skip reporting if Sentry is not initialized
  try {
    // First check if basic Sentry functionality is available
    if (typeof Sentry.captureException !== 'function') {
      return;
    }
  } catch (_) {
    // If there's any error accessing Sentry, don't report
    return;
  }

  // Make sure we have an Error object
  const errorObject =
    error instanceof Error
      ? error
      : new Error(typeof error === 'string' ? error : JSON.stringify(error));

  Sentry.withScope(scope => {
    // Set the level
    scope.setLevel(level);

    // Add context tags
    Object.entries(context).forEach(([key, value]) => {
      scope.setExtra(key, value);
    });

    // Set user info if available
    const user = context.user || context.userId || null;
    if (user) {
      scope.setUser(typeof user === 'string' ? { id: user } : user);
    }

    // Set transaction name if available
    if (context.transaction) {
      scope.setTransactionName(String(context.transaction));
    }

    // Report the error
    Sentry.captureException(errorObject);
  });
}

/**
 * Add a breadcrumb to the current Sentry scope
 * @param message The breadcrumb message
 * @param category The breadcrumb category
 * @param level The severity level
 * @param data Additional data
 */
export function addBreadcrumb(
  message: string,
  category?: string,
  level: Sentry.SeverityLevel = 'info',
  data?: Record<string, unknown>
): void {
  // Skip if Sentry is not initialized
  try {
    // First check if basic Sentry functionality is available
    if (typeof Sentry.addBreadcrumb !== 'function') {
      return;
    }
  } catch (_) {
    // If there's any error accessing Sentry, don't report
    return;
  }

  Sentry.addBreadcrumb({
    message,
    category,
    level,
    data,
  });
}
